import { useEffect, useMemo, useState } from "react";

import { usePageNameContext } from "../../../contexts/PageTitleContext";
import useSettings from "../../../data/useSettings";
import useDefaultCRUDHandlers from "../../../hooks/useDefaultCRUDHandlers";
import { HISTORY_FIELDS_KEY } from "../../../hooks/usePartHistoryEnabled";
import { ISetting } from "../../../models/Setting";
import { ButtonInput } from "../../../components/shared/Inputs/ButtonInput";
import LoadingWheel from "../../../components/shared/LoadingWheel";

type FieldGroup = {
  title: string;
  description: string;
  fields: { field: string; label: string; hint: string; heavy?: boolean }[];
};

// A field name is matched against both the part and its listings - Title, CategoryId, Quantity and
// Description all exist on each - so this is one flat list grouped by what a field tells you, not
// by which table it lives on. The API names the difference in the event itself ("Listing title
// changed" against "Title changed"), so one tick covers both.
const FIELD_GROUPS: FieldGroup[] = [
  {
    title: "Interventions",
    description:
      "The work someone did to try and shift the part. This is what the history card is for - leave these on unless you have a reason not to.",
    fields: [
      { field: "Title", label: "Title", hint: "Listing and part title edits" },
      { field: "PartNumber", label: "MPN", hint: "Manufacturer part number added or corrected" },
      { field: "OEPartNumber", label: "OE MPN", hint: "OE part number added or corrected" },
      { field: "BuyItNowPrice", label: "Buy It Now price", hint: "Discounts and price rises on the listing" },
      { field: "AuctionStartPrice", label: "Auction start price", hint: "Discounts and price rises on an auction" },
      { field: "EnteredPrice", label: "Entered price", hint: "The price set on the part itself" },
      { field: "ScrapOut", label: "Scrap out decision", hint: "Keep / Discount / Scrap" },
      { field: "ScrapOutManual", label: "Manual scrap out", hint: "Sent to scrap out by hand rather than by the nightly sweep" },
    ],
  },
  {
    title: "Lifecycle and sale",
    description: "Where the part got to. These also feed the Stock page's part history drawer.",
    fields: [
      { field: "PartStatus", label: "Part status", hint: "Status transitions" },
      { field: "SoldDate", label: "Sold", hint: "Sold, and sales reversed by a return" },
      { field: "OrderId", label: "Order", hint: "Order linked, changed or cleared" },
      { field: "InvoiceId", label: "Invoice", hint: "Invoice linked or unlinked" },
      { field: "ListedDate", label: "Listed", hint: "First listed, or the listed date cleared" },
      { field: "PublishedOn", label: "Published to eBay", hint: "Published, or withdrawn" },
      { field: "ListingEnded", label: "Listing ended", hint: "The listing coming down" },
      { field: "RelistCount", label: "Relisted", hint: "Each relist of the part" },
      { field: "SaleSource", label: "Sale source", hint: "Which channel it sold through" },
      { field: "WarehouseLocationId", label: "Location", hint: "Location set, changed or cleared - named moves live on the location history" },
      { field: "Sku", label: "SKU", hint: "SKU changes" },
      { field: "EbayId", label: "eBay offer id", hint: "The active offer pointing at a different listing" },
      { field: "EbayItemNumber", label: "eBay item number", hint: "Useful when a part looks like it has two live listings" },
      { field: "EbayTradingApiItemId", label: "eBay trading item id", hint: "Useful when a part looks like it has two live listings" },
      { field: "EbayInventoryApiOfferId", label: "eBay inventory offer id", hint: "Useful when a part looks like it has two live listings" },
    ],
  },
  {
    title: "Other fields",
    description:
      "Off by default because they are either noisy or large. Turning one on adds a plain \"field: old → new\" row wherever it changed.",
    fields: [
      { field: "Price", label: "Part price", hint: "The price held on the part rather than the listing" },
      { field: "CategoryId", label: "Category", hint: "Recategorisations - shows as ids, not names" },
      { field: "Condition", label: "Condition", hint: "eBay condition changes" },
      { field: "Quantity", label: "Quantity", hint: "Stock quantity on the listing" },
      { field: "EnableBestOffer", label: "Best offer enabled", hint: "Best offer turned on or off" },
      { field: "FulfillmentPolicyId", label: "Shipping policy", hint: "Policy swaps - shows as ids, not names" },
      { field: "ReturnPolicyId", label: "Returns policy", hint: "Policy swaps - shows as ids, not names" },
      { field: "PaymentPolicyId", label: "Payment policy", hint: "Policy swaps - shows as ids, not names" },
      {
        field: "ConditionDescription",
        label: "Condition description",
        hint: "Long text - each change is trimmed to the first 200 characters",
        heavy: true,
      },
      {
        field: "Description",
        label: "Listing description",
        hint: "The full description document. Rewritten in bulk by description resets, so this fills the history with noise",
        heavy: true,
      },
    ],
  },
];

// Must match DefaultHistoryFields in PartHistoryService - the API falls back to its own copy when
// this setting is absent, and the two disagreeing would make the page lie about what is being shown.
const DEFAULT_FIELDS = [
  "Title",
  "PartNumber",
  "OEPartNumber",
  "BuyItNowPrice",
  "AuctionStartPrice",
  "EnteredPrice",
  "ScrapOut",
  "ScrapOutManual",
  "SoldDate",
  "OrderId",
  "InvoiceId",
  "PartStatus",
  "WarehouseLocationId",
  "ListedDate",
  "PublishedOn",
  "ListingEnded",
  "SaleSource",
  "Sku",
  "RelistCount",
  "EbayId",
  "EbayItemNumber",
  "EbayTradingApiItemId",
  "EbayInventoryApiOfferId",
];

const parseFields = (value?: string): string[] => {
  if (!value) return DEFAULT_FIELDS;

  try {
    const parsed = JSON.parse(value);

    if (!Array.isArray(parsed)) return DEFAULT_FIELDS;

    const fields = parsed.filter((x): x is string => typeof x === "string" && x.trim().length > 0);

    // An empty list reads as "not configured" on the API side too, so show the defaults rather
    // than an empty page that suggests nothing is being recorded.
    return fields.length > 0 ? fields : DEFAULT_FIELDS;
  } catch {
    return DEFAULT_FIELDS;
  }
};

const sameFields = (a: string[], b: string[]) =>
  a.length === b.length && [...a].sort().join("|") === [...b].sort().join("|");

export default function PartHistoryFieldsEditor() {
  const { setInfo } = usePageNameContext();
  const { generalSettings, loading, update } = useSettings();
  const { saveHandlers } = useDefaultCRUDHandlers("Part History Fields");
  const setting = generalSettings?.find((s) => s.key === HISTORY_FIELDS_KEY);
  const savedFields = useMemo(() => parseFields(setting?.value), [setting?.value]);

  const [selected, setSelected] = useState<string[]>(DEFAULT_FIELDS);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setInfo({
      name: "Part History Fields",
      desc: "Choose which changes appear in a part's history",
    });
  }, [setInfo]);

  useEffect(() => {
    if (!loading) setSelected(savedFields);
  }, [loading, savedFields]);

  const isDirty = !sameFields(selected, savedFields);
  const isDefault = sameFields(selected, DEFAULT_FIELDS);

  const toggle = (field: string) =>
    setSelected((current) =>
      current.includes(field) ? current.filter((x) => x !== field) : [...current, field]
    );

  const onSave = async () => {
    if (!setting?.id) return;

    setSaving(true);

    try {
      await update.mutateAsync(
        { ...setting, value: JSON.stringify(selected) } as ISetting,
        saveHandlers
      );
    } catch {
      // handled by CRUD handlers
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-2">
        <LoadingWheel />
      </div>
    );
  }

  return (
    <div className="p-2 space-y-8">
      <div>
        <h1 className="text-lg font-medium text-gray-900">Part History Fields</h1>
        <p className="text-sm text-gray-600 mt-1">
          Controls which changes show up in the History card on the Active Listings expanded row, and
          in the part history drawer on the Stock page. Every change a part or its listings go
          through is recorded either way — this only decides what is worth reading back.
        </p>
        <p className="text-sm text-gray-600 mt-2">
          Unticking a field never hides the event it caused: a price cut is still reported as{" "}
          <span className="italic">"Price reduced"</span>, you just don't get the old and new values
          alongside it. Long values are trimmed to the first 200 characters whatever you pick.
        </p>
        {!setting?.id && (
          <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
            <p className="font-medium">This setting doesn't exist for this customer yet.</p>
            <p className="mt-1">
              Nothing is broken - the history below is running on the built-in defaults, which are the
              ticks you can see. To make it editable here, add the row in Betty:
            </p>
            <dl className="mt-2 grid grid-cols-[auto,1fr] gap-x-3 gap-y-1 font-mono text-xs">
              <dt className="text-amber-800">Key</dt>
              <dd className="break-all">{HISTORY_FIELDS_KEY}</dd>
              <dt className="text-amber-800">Value</dt>
              <dd className="break-all">{JSON.stringify(DEFAULT_FIELDS)}</dd>
              <dt className="text-amber-800">Input type</dt>
              <dd>Text</dd>
              <dt className="text-amber-800">Setting type</dt>
              <dd>General</dd>
              <dt className="text-amber-800">Visible to customer</dt>
              <dd>true</dd>
            </dl>
            <p className="mt-2">
              Once it's there, reload this page and the ticks below become editable.
            </p>
          </div>
        )}
      </div>

      <div className="space-y-6 max-w-3xl">
        {FIELD_GROUPS.map((group) => (
          <div key={group.title} className="rounded-md border border-gray-200 bg-white p-3">
            <h2 className="text-sm font-semibold text-gray-900">{group.title}</h2>
            <p className="text-xs text-gray-500 mt-0.5">{group.description}</p>

            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
              {group.fields.map((f) => (
                <label key={f.field} className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selected.includes(f.field)}
                    onChange={() => toggle(f.field)}
                    className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="min-w-0">
                    <span className="block text-sm font-medium text-gray-700">
                      {f.label}
                      {f.heavy && (
                        <span className="ml-1.5 rounded bg-amber-50 px-1 py-0.5 text-[10px] font-medium text-amber-800 ring-1 ring-inset ring-amber-200">
                          noisy
                        </span>
                      )}
                    </span>
                    <span className="block text-xs text-gray-500">{f.hint}</span>
                    <span className="block font-mono text-[10px] text-gray-400">{f.field}</span>
                  </span>
                </label>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-3">
        <ButtonInput
          label="Save"
          isSubmit={false}
          onClick={onSave}
          classes={""}
          disabled={!isDirty || !setting?.id}
          isLoading={saving}
        />
        <ButtonInput
          label="Reset to defaults"
          isSubmit={false}
          onClick={() => setSelected(DEFAULT_FIELDS)}
          classes={""}
          disabled={isDefault}
        />
        <span className="text-xs text-gray-500">
          {selected.length} field{selected.length === 1 ? "" : "s"} selected
          {isDefault ? " (the defaults)" : ""}
          {isDirty ? " — unsaved" : ""}
        </span>
      </div>
    </div>
  );
}
